import { Flex } from '@chakra-ui/react';
import { DateIntervalSelect } from 'components/reporting/DateIntervalSelect';
import { ReportSpecChart } from 'components/reporting/ReportSpecChart';
import { dateIntervalOptions, getSharedChartOptions } from 'components/reporting/utils';
import { AxisLabelsFormatterContextObject, dateFormat, Options } from 'highcharts/highstock';
import { merge } from 'lodash';
import { useState, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { StandardReportInfo, DateInterval } from 'types/reporting';

const ITEM_TYPE_CHART = 'RevDistribution';

export const chartOptions = (intl: IntlShape, dateInterval?: DateInterval): Options => {
    const dateFormatStr = dateInterval === 'Yr' ? '%Y' : '%Y-%m';
    return merge({}, getSharedChartOptions(intl), {
        exporting: {
            csv: {
                dateFormat: dateFormatStr,
            }
        },
        yAxis: {
            title: {
                text: '$',
            },
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: (context: AxisLabelsFormatterContextObject) => {
                    return dateFormat(dateFormatStr, Number(context.value));
                },
            },
        },
        rangeSelector: {
            enabled: true,
            inputEnabled: false,
            buttons: [{ type: 'all', text: 'All', title: 'View all' }],
            selected: 4,
        },
        legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemMarginTop: 10,
            itemMarginBottom: 10,
            padding: 40,
        },
        tooltip: {
            shared: false,
            split: false,
            xDateFormat: dateFormatStr,
            pointFormatter: function () {
                if (!!this.y) {
                    const value = Math.round(this.y).toLocaleString('en-US');
                    return `<span style="color:${this.color}">${this.series.name}</span>: <b>$${value}</b><br/>`;
                }
                return '';
            },
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
            },
        },
    });
};

interface PortfolioRevDistributionProps {
    info: StandardReportInfo;
}

export function PortfolioRevDistribution({ info }: PortfolioRevDistributionProps) {
    const intl = useIntl();
    const [selectedInterval, setSelectedInterval] = useState<DateInterval>(dateIntervalOptions?.[0]?.value);
    const [selectedItem] = useState<number>(info.items?.[0]?.itemId);

    const studyId = info.studyId;

    const chartSpec = useMemo(() => {
        return info?.itemTypeCharts?.[ITEM_TYPE_CHART];
    }, [info]);

    const options = useMemo(() => {
        return chartOptions(intl, selectedInterval);
    },[intl, selectedInterval])

    return (
        <Flex height={'full'} flexDir={'column'}>
            <Flex alignItems={'center'} p={4} gap={6}>
                <DateIntervalSelect value={selectedInterval} onChange={setSelectedInterval} />
            </Flex>
            <ReportSpecChart
                reportType="PortfolioEnergyAndRev"
                studyId={studyId}
                itemId={selectedItem}
                dateInterval={selectedInterval}
                spec={chartSpec}
                initialOptions={options}
            />
        </Flex>
    );
}
